import { Edit } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { useState } from "react";
import Loader from "../utils/Loader";
import Logger from "../../../utils/Logger";

interface EditActionsProps {
  editMode: boolean;
  editModeContext: boolean;
  setEditMode: (mode: boolean) => void;
  hasData: boolean;
  isLoading: boolean;
  onCancel?: () => void;
  onDelete?: () => void;
  canSubmit?: boolean;
  canEnterEditMode?: boolean;
}

export function FormActions({
  editMode,
  editModeContext,
  setEditMode,
  hasData,
  isLoading,
  onCancel,
  onDelete,
  canSubmit = true,
  canEnterEditMode = true,
}: EditActionsProps) {
  const [showErrors, setShowErrors] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formState } = useFormContext();

  const hasErrors = Object.keys(formState.errors).length > 0;

  if (hasErrors) {
    Logger.error(formState.errors);
  }

  return (
    <div className="select-none sticky pt-2 pb-3 bottom-0 bg-white dark:bg-gray-800">
      {hasErrors && showErrors && (
        <p className="flex items-center justify-end mb-2 text-sm text-red-600 dark:text-red-400">
          {t("form.validationErrors")}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-4">
        {editMode ? (
          <>
            {!editModeContext && (
              <button
                type="button"
                onClick={() =>
                  onCancel
                    ? onCancel()
                    : hasData
                    ? setEditMode(false)
                    : navigate(-1)
                }
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md
                       dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              >
                {t("form.back")}
              </button>
            )}
            {hasData && onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md 
                         dark:bg-gray-800 dark:border-red-600 dark:text-red-400"
              >
                {t("form.delete")}
              </button>
            )}
            {canSubmit && (
              <button
                type="submit"
                onClick={() => setShowErrors(true)}
                className="flex items-center justify-center gap-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md
                       dark:bg-blue-500"
              >
                {isLoading && <Loader size={4} />}
                {hasData ? t("form.update") : t("form.create")}
              </button>
            )}
          </>
        ) : (
          <>
            {onCancel && (
              <button
                type="button"
                onClick={() => onCancel()}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md
                         dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              >
                {t("form.back")}
              </button>
            )}
            {canEnterEditMode && (
              <button
                type="button"
                onClick={() => setEditMode(true)}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md
                       dark:bg-blue-500"
              >
                <Edit className="w-4 h-4 me-2" />
                {t("form.edit")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
